// Display formatting for the HUD: dates, turns, compact numbers, shares, data age.

import { EPOCH_START, daysBetween, yearOf } from '../core/dates.ts';
import type { DateStr } from '../core/schema.ts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** One turn is one week (ARCHITECTURE §3.5). */
export const TURN_DAYS = 7;

/** Truth rule 5: values older than this many years are greyed and show their year. */
export const STALE_YEARS = 3;

/** '2026-09-28' → '28 Sep 2026'. */
export function fmtDate(d: DateStr): string {
  const [y, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/** '2026-09' → 'Sep 2026'. */
export function fmtMonth(month: string): string {
  const [y, m] = month.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

/** '2026-09-28' → '28 September 2026'. */
export function fmtDateLong(d: DateStr): string {
  const [y, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS_LONG[Number(m) - 1]} ${y}`;
}

/** Turn number of a date: turn 1 is the week starting 2000-01-01. */
export function turnOf(d: DateStr): number {
  return Math.floor(daysBetween(EPOCH_START, d) / TURN_DAYS) + 1;
}

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * Population years from here on are modelled (GHS urban centre database, 2025 epoch), not counted.
 * The map banners apply the same rule (view/political/labels.ts); format.test.ts guards the pair.
 */
export const MODELLED_FROM = 2025;
export const MODELLED_TAG = 'est.';
export const MODELLED_NOTE = 'Modelled estimate (GHS urban centre database), not a census count';

/** 'est.' for a modelled population year, else undefined. */
export function modelledTag(year: number | undefined): string | undefined {
  return year !== undefined && year >= MODELLED_FROM ? MODELLED_TAG : undefined;
}

/** Source chip label: the data year (with its tag), else a counted "N sources" (a bare "9129" reads as a year). */
export function chipText(year: number | undefined, count: number, tag?: string): string {
  if (year !== undefined) {
    return tag ? `${year} ${tag}` : String(year);
  }

  return count > 1 ? `${fmtInt(count)} sources` : '';
}

// Compact suffixes, largest first: 30.5T, 347M, 12.3K.
const COMPACT: Array<[number, string]> = [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']];

export function fmtCompact(n: number): string {
  const abs = Math.abs(n);
  for (const [scale, suffix] of COMPACT) {
    if (abs < scale) {
      continue;
    }
    const v = n / scale;
    const digits = Math.abs(v) >= 100 ? 0 : 1;
    return `${v.toFixed(digits)}${suffix}`;
  }
  return abs >= 10 ? n.toFixed(0) : n.toFixed(1);
}

/** 0..1 share → '24.1%'. */
export function fmtShare(share: number, digits = 1): string {
  return `${(share * 100).toFixed(digits)}%`;
}

const INDEX_DIGITS = 3;

// Units whose value must keep the unit on screen and its precision: a compacted 7.0K would
// read as 7 thousand of nothing, and 0.938 rounded to 0.9 loses the index.
const UNIT_FORMAT: Record<string, (v: number) => string> = {
  'pflop/s': (v) => `${fmtInt(v)} PFlop/s`,
  twh: (v) => `${fmtInt(v)} TWh`,
  'tonnes per person': (v) => `${v.toFixed(1)} t`,
  years: (v) => `${v.toFixed(1)} yr`,
  index: (v) => v.toFixed(INDEX_DIGITS),
};

/** Format a stat by its unit string, e.g. 'current US$' → '$30.5T', '%' → '3.4%'. */
export function fmtStat(value: number, unit: string): string {
  const u = unit.toLowerCase();
  const exact = UNIT_FORMAT[u];
  if (exact) {
    return exact(value);
  }
  if (u.includes('share') || u === '0..1') {
    return fmtShare(value);
  }
  if (u.includes('%') || u.includes('percent')) {
    return `${value.toFixed(1)}%`;
  }
  if (u.includes('us$') || u.includes('usd') || u.includes('$')) {
    return `$${fmtCompact(value)}`;
  }
  return fmtCompact(value);
}

/** True when a data year is older than the stale threshold at date `d`. */
export function isStale(year: number, d: DateStr): boolean {
  return yearOf(d) - year > STALE_YEARS;
}

/** True when any dated item comes after date `d`: the world as of `d` had not said it yet. */
export function laterThan(items: Array<{ date: DateStr }>, d: DateStr): boolean {
  return items.some((i) => i.date > d);
}
